import { computed } from "vue";
import { BlockDef } from "./block.interfaces";
import {
    createBlock,
    textField,
    descriptiveImageField,
    headingField,
    htmlField,
} from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import {
    ThreeColumn,
    TwoRowBentoGrid,
    type ThreeColumnProps,
    type TwoRowBentoGridProps,
} from "@/views/blocks/bento/bento";
import { getPaceholderImageUrl } from "@/apps/page/services/page.service";
import { useGetters } from "@/store/vuex-composition-mappers";

export function useBentos() {
    const { colorDefaults } = useDefaultColors();

    // @ts-expect-error TODO: Fix typing in the future
    const { pageColors } = useGetters("page", ["pageColors"]);

    const threeColumnBlock = computed<BlockDef>(() =>
        createBlock<ThreeColumnProps>({
            typeId: "ThreeColumn",
            type: "bentoGrid",
            name: "Three Column Bento Grid",
            component: ThreeColumn,
        })
            .withField("tag", textField("Tag", "Deploy faster"))
            .withField("title", headingField("Title", "Everything you need to deploy your app"))
            .withField(
                "leftColumnTitle",
                textField("Title (left column)", "Mobile friendly", "Title")
            )
            .withField(
                "leftColumnDescription",
                htmlField(
                    "Description (left column)",
                    "Responsive design that adapts to any device for a consistent experience across platforms.",
                    "Description"
                )
            )
            .withField(
                "leftColumnImage",
                descriptiveImageField("Image (left column)", {
                    src: getPaceholderImageUrl("540/960", pageColors.value.brand),
                    alt: "Mobile friendly interface",
                })
            )
            .withField(
                "topCenterColumnTitle",
                textField("Title (top center column)", "Performance", "Title")
            )
            .withField(
                "topCenterColumnDescription",
                htmlField(
                    "Description (top center column)",
                    "Optimized for speed with lightning-fast load times for exceptional user experiences.",
                    "Description"
                )
            )
            .withField(
                "topCenterColumnImage",
                descriptiveImageField("Image (top center column)", {
                    src: getPaceholderImageUrl("600/300", pageColors.value.brand),
                    alt: "Performance metrics",
                })
            )
            .withField(
                "bottomCenterColumnTitle",
                textField("Title (bottom center column)", "Security", "Title")
            )
            .withField(
                "bottomCenterColumnDescription",
                htmlField(
                    "Description (bottom center column)",
                    "Enterprise-grade protection with advanced encryption and industry compliance.",
                    "Description"
                )
            )
            .withField(
                "bottomCenterColumnImage",
                descriptiveImageField("Image (bottom center column)", {
                    src: getPaceholderImageUrl("600/300", pageColors.value.brand),
                    alt: "Security features",
                })
            )
            .withField(
                "rightColumnTitle",
                textField("Title (right column)", "Powerful APIs", "Title")
            )
            .withField(
                "rightColumnDescription",
                htmlField(
                    "Description (right column)",
                    "Flexible integration options with comprehensive documentation for simple connectivity.",
                    "Description"
                )
            )
            .withField(
                "rightColumnImage",
                descriptiveImageField("Image (right column)", {
                    src: getPaceholderImageUrl("540/960", pageColors.value.brand),
                    alt: "API code example",
                })
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
                colorDefaults.value.cardBackground,
                colorDefaults.value.cardText,
            ])
            .build()
    );

    const twoRowBentoBlock = computed<BlockDef>(() =>
        createBlock<TwoRowBentoGridProps>({
            typeId: "TwoRowBentoGrid",
            type: "bentoGrid",
            name: "Two Row Bento Grid",
            component: TwoRowBentoGrid,
        })
            // Header fields
            .withField("tag", textField("Tag", "Features"))
            .withField("title", headingField("Title", "Everything you need in one place"))

            // Top Left Card
            .withField("topLeftCardTag", textField("Tag (Top Left)", "Analytics", "Tag"))
            .withField("topLeftCardTitle", headingField("Title (Top Left)", "Data Insights"))
            .withField(
                "topLeftCardDescription",
                htmlField(
                    "Description (Top Left)",
                    "Powerful analytics tools to help you make data-driven decisions.",
                    "Description"
                )
            )
            .withField(
                "topLeftCardImage",
                descriptiveImageField("Image (Top Left)", {
                    src: getPaceholderImageUrl("620/320", pageColors.value.brand),
                    alt: "Analytics dashboard",
                })
            )

            // Top Right Card
            .withField("topRightCardTag", textField("Tag (Top Right)", "Automation", "Tag"))
            .withField(
                "topRightCardTitle",
                headingField("Title (Top Right)", "Workflow Automation")
            )
            .withField(
                "topRightCardDescription",
                htmlField(
                    "Description (Top Right)",
                    "Automate repetitive tasks and streamline your workflow.",
                    "Description"
                )
            )
            .withField(
                "topRightCardImage",
                descriptiveImageField("Image (Top Right)", {
                    src: getPaceholderImageUrl("620/320", pageColors.value.brand),
                    alt: "Automation workflow",
                })
            )

            // Bottom Left Card
            .withField("bottomLeftCardTag", textField("Tag (Bottom Left)", "Integration", "Tag"))
            .withField(
                "bottomLeftCardTitle",
                headingField("Title (Bottom Left)", "API Integration")
            )
            .withField(
                "bottomLeftCardDescription",
                htmlField(
                    "Description (Bottom Left)",
                    "Connect with your favorite tools and services seamlessly.",
                    "Description"
                )
            )
            .withField(
                "bottomLeftCardImage",
                descriptiveImageField("Image (Bottom Left)", {
                    src: getPaceholderImageUrl("620/320", pageColors.value.brand),
                    alt: "API integration",
                })
            )

            // Bottom Center Card
            .withField("bottomCenterCardTag", textField("Tag (Bottom Center)", "Security", "Tag"))
            .withField(
                "bottomCenterCardTitle",
                headingField("Title (Bottom Center)", "Enterprise Security")
            )
            .withField(
                "bottomCenterCardDescription",
                htmlField(
                    "Description (Bottom Center)",
                    "Enterprise-grade security features to protect your data.",
                    "Description"
                )
            )
            .withField(
                "bottomCenterCardImage",
                descriptiveImageField("Image (Bottom Center)", {
                    src: getPaceholderImageUrl("620/320", pageColors.value.brand),
                    alt: "Security features",
                })
            )

            // Bottom Right Card
            .withField("bottomRightCardTag", textField("Tag (Bottom Right)", "Support", "Tag"))
            .withField("bottomRightCardTitle", headingField("Title (Bottom Right)", "24/7 Support"))
            .withField(
                "bottomRightCardDescription",
                htmlField(
                    "Description (Bottom Right)",
                    "Round-the-clock support to help you succeed.",
                    "Description"
                )
            )
            .withField(
                "bottomRightCardImage",
                descriptiveImageField("Image (Bottom Right)", {
                    src: getPaceholderImageUrl("620/320", pageColors.value.brand),
                    alt: "Customer support",
                })
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    return {
        blocks: computed(() => [threeColumnBlock.value, twoRowBentoBlock.value]),
    };
}
